(function () {
    'use strict';

    // Glue for the public vehicle lookup (_VehicleRegInput with RequireTurnstile = true). Rendering is
    // delegated to window.SmaoinTurnstile (Smaoin.Shared.Ui's turnstile-widget.js); this file only attaches the solved token to the
    // Check button's HTMX request. Tokens are single-use, so the widget re-solves after every lookup, and a
    // click that lands before a token is ready is held and replayed once one arrives.
    const CONTAINER_ID = 'cf-turnstile-container-vehicle';
    const INPUT_ID     = 'cf-turnstile-response-vehicle';
    const ACTION       = 'vehicle-lookup';

    // Arriving from the homepage's "Check my reg" (#van-health-result, on "Here's your car"): the browser jumps as
    // soon as the target exists, then fonts and images above it load and push it down, so the visitor lands a
    // little short, looking at the Check button. Jump again once everything has loaded.
    if (location.hash === '#van-health-result') {
        window.addEventListener('load', function () {
            const target = document.getElementById('van-health-result');
            if (target) target.scrollIntoView({ block: 'start' });
        }, { once: true });
    }

    const container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    // Space for the result, taken at the click (see .is-reserved in site.css): a move within 500ms of a click is not a
    // layout shift, the result arriving a second later into space it already has is not one either.
    const resultArea = document.getElementById('van-health-result-area');
    const result     = document.getElementById('dvla-lookup-result');

    function reserve() {
        if (!resultArea || !result) return;
        resultArea.classList.add('is-reserved');
        const checking = document.createElement('div');
        checking.className = 'van-health-checking';
        checking.setAttribute('role', 'status');
        const spinner = document.createElement('div');
        spinner.className = 'van-health-checking__spinner';
        spinner.setAttribute('aria-hidden', 'true');
        const text = document.createElement('p');
        text.textContent = 'Checking DVLA and DVSA records…';
        checking.append(spinner, text);
        result.replaceChildren(checking);
    }

    // Nothing came back to fill it (an error is shown beside the reg box instead). Giving the space back NOW would pull
    // the page up a second after the click - a layout shift of its own (0.29, measured). So it is given back at the
    // visitor's next tap or key, when a move is expected; a tap on Check takes it again in the same frame.
    function release() {
        if (!resultArea || !result) return;
        if (result.querySelector('.van-health-checking')) result.replaceChildren();
        if (result.firstElementChild || !resultArea.classList.contains('is-reserved')) return;
        const giveBack = e => {
            if (!e.isTrusted) return;
            document.removeEventListener('pointerdown', giveBack, true);
            document.removeEventListener('keydown', giveBack, true);
            if (!result.firstElementChild) resultArea.classList.remove('is-reserved');
        };
        document.addEventListener('pointerdown', giveBack, true);
        document.addEventListener('keydown', giveBack, true);
    }

    document.addEventListener('click', e => {
        const btn = e.target instanceof Element ? e.target.closest('[data-vehicle-lookup]') : null;
        if (btn && e.isTrusted) reserve();
    }, true);

    let tokenReady = false;
    let pending    = null;   // the Check button whose click is waiting on a token
    const tickHint = document.getElementById('vehicle-tick-hint');

    // Cloudflare wants a tick (turnstile-tick.js): nothing is being checked until it gets one, so the button and the
    // result stop saying "Checking…" and the hint says what to do. The click stays held - the tick sends it.
    function askForTick() {
        if (!pending) return;
        setWaiting(pending, false);
        release();
        if (tickHint) tickHint.hidden = false;
        if (container.scrollIntoView) container.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    function isLookup(elt) {
        return !!(elt && elt.hasAttribute && elt.hasAttribute('data-vehicle-lookup'));
    }

    function setWaiting(btn, waiting) {
        if (!btn) return;
        if (waiting) {
            if (!btn.dataset.label) btn.dataset.label = btn.textContent;
            btn.textContent = 'Checking…';
            btn.setAttribute('aria-busy', 'true');
        } else {
            if (btn.dataset.label) btn.textContent = btn.dataset.label;
            btn.removeAttribute('aria-busy');
        }
    }

    function showError(message) {
        const el = document.getElementById('reg-error');
        if (!el) return;
        el.className = 'callout-stripe callout-stripe--error text-sm mt-2';
        el.replaceChildren();
        const icon = document.createElement('i');
        icon.className = 'fa-solid fa-triangle-exclamation flex-shrink-0 text-sign-red mt-0.5';
        const text = document.createElement('div');
        text.textContent = message;
        el.append(icon, text);
    }

    const widget = window.SmaoinTurnstile.render(container, INPUT_ID, {
        action: ACTION,
        onToken: () => {
            tokenReady = true;
            if (tickHint) tickHint.hidden = true;
            if (pending) {
                const btn = pending;
                pending = null;
                btn.click();
            }
        },
        onExpire: () => { tokenReady = false; },
        onFail: (code) => {
            tokenReady = false;
            // api.js never loaded (no code) — nothing will ever arrive, so release the held click.
            if (code === undefined && pending) {
                setWaiting(pending, false);
                pending = null;
                release();
                showError('The security check could not load. Please disable any blocker for this page and try again.');
            }
        }
    });

    document.body.addEventListener('htmx:configRequest', e => {
        if (!isLookup(e.detail.elt)) return;

        const input = document.getElementById(INPUT_ID);
        if (tokenReady && input && input.value) {
            e.detail.parameters['cf-turnstile-response'] = input.value;
            tokenReady = false;
            setWaiting(e.detail.elt, true);
        } else {
            e.preventDefault();
            pending = e.detail.elt;
            setWaiting(pending, true);
            if (window.SyvTurnstileTick.showing(container)) askForTick();   // the box was already up
        }
    });
    window.SyvTurnstileTick.watch(container, askForTick);

    // Token spent (whatever the outcome) — clear it and solve a fresh one for the next lookup.
    document.body.addEventListener('htmx:afterRequest', e => {
        if (!isLookup(e.detail.elt)) return;
        setWaiting(e.detail.elt, false);
        const status = e.detail.xhr && e.detail.xhr.status;
        if (status === 429) {
            showError('Too many vehicle checks have been made recently. Please try again later.');
        } else if (!e.detail.successful) {
            showError('Vehicle lookup is temporarily unavailable. Please try again in a moment.');
        }
        const input = document.getElementById(INPUT_ID);
        if (input) input.value = '';
        tokenReady = false;
        widget.reset();
        // After the swap has had its turn: a failed request swaps nothing, an error swaps in nothing.
        setTimeout(release, 100);
    });
}());
